import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { PageHeader, Section, EmptyState } from "@/components/primitives";
import { Modal, Field, inputCls, PrimaryButton, GhostButton } from "@/components/Modal";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Trash, DownloadSimple } from "@phosphor-icons/react";

const AUDIENCE_OPTS = ["internal", "investor", "creator"];
const ROLE_OPTS = ["admin", "editor", "viewer"];

function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const save = async () => {
    if (form.next !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (form.next.length < 10) { toast.error("Minimum 10 characters"); return; }
    try {
      await api.post("/api/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed");
      setOpen(false); setForm({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <>
      <div className="border border-[#2C2C2A] bg-[#1A1A18] p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[#F9F9F8]">Password</div>
            <p className="mt-1 text-sm text-[#A1A19D]">Change your account password. This is the secure, authenticated way to rotate credentials — the owner password set during bootstrap should be rotated here.</p>
          </div>
          <PrimaryButton data-testid="change-password-btn" onClick={() => setOpen(true)}>Change password</PrimaryButton>
        </div>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Change password" testid="change-password-modal">
        <Field label="Current password"><input type="password" className={inputCls} data-testid="cp-current" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} /></Field>
        <Field label="New password"><input type="password" className={inputCls} data-testid="cp-next" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} /></Field>
        <Field label="Confirm new password"><input type="password" className={inputCls} data-testid="cp-confirm" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></Field>
        <div className="flex justify-end gap-2"><GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="cp-save" onClick={save}>Save</PrimaryButton></div>
      </Modal>
    </>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer", audiences: ["internal"], password: "" });
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const load = useCallback(async () => { const { data } = await api.get("/api/users"); setUsers(data); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { await api.post("/api/users", form); toast.success("User created"); setCreating(false); setForm({ email: "", name: "", role: "viewer", audiences: ["internal"], password: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const updateRole = async (u, role) => { try { await api.put(`/api/users/${u.id}`, { role }); toast.success("Role updated"); load(); } catch { toast.error("Failed"); } };
  const toggleAudience = async (u, aud) => {
    const has = u.audiences.includes(aud);
    const next = has ? u.audiences.filter((a) => a !== aud) : [...u.audiences, aud];
    try { await api.put(`/api/users/${u.id}`, { audiences: next }); toast.success("Audience updated"); load(); } catch { toast.error("Failed"); }
  };
  const toggleActive = async (u) => { try { await api.put(`/api/users/${u.id}`, { active: !u.active }); load(); } catch { toast.error("Failed"); } };
  const doReset = async () => {
    if (resetPw.length < 10) { toast.error("Minimum 10 characters"); return; }
    try { await api.post(`/api/users/${resetFor.id}/reset-password`, { new_password: resetPw }); toast.success("Password reset"); setResetFor(null); setResetPw(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.email}?`)) return; try { await api.delete(`/api/users/${u.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } };

  const toggleFormAudience = (aud) => {
    const has = form.audiences.includes(aud);
    setForm({ ...form, audiences: has ? form.audiences.filter((a) => a !== aud) : [...form.audiences, aud] });
  };

  return (
    <Section title="Users & access" action={<PrimaryButton data-testid="user-add-btn" onClick={() => setCreating(true)}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add user</span></PrimaryButton>}>
      <div className="overflow-x-auto border border-[#2C2C2A]">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="border-b border-[#2C2C2A] text-left">
            <th className="cm-label p-3">User</th><th className="cm-label p-3">Write role</th><th className="cm-label p-3">Audience scopes</th><th className="cm-label p-3">Active</th><th className="cm-label p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[#2C2C2A] last:border-0" data-testid={`user-row-${u.email}`}>
                <td className="p-3"><div className="text-[#F9F9F8]">{u.name}</div><div className="cm-label">{u.email}</div></td>
                <td className="p-3">
                  <select value={u.role} onChange={(e) => updateRole(u, e.target.value)} data-testid={`user-role-${u.email}`}
                    className="rounded-sm border border-[#2C2C2A] bg-[#121211] px-2 py-1 text-xs text-[#F9F9F8]">
                    {ROLE_OPTS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {AUDIENCE_OPTS.map((a) => (
                      <button key={a} onClick={() => toggleAudience(u, a)}
                        className={`rounded-sm border px-2 py-0.5 text-xs ${u.audiences.includes(a) ? "border-[#F59E0B] bg-[rgba(245,158,11,0.12)] text-[#F59E0B]" : "border-[#2C2C2A] text-[#A1A19D]"}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="p-3"><button onClick={() => toggleActive(u)}><StatusBadge status={u.active ? "Active" : "Disabled"} /></button></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={() => setResetFor(u)} className="text-xs text-[#F59E0B] hover:underline" data-testid={`user-reset-${u.email}`}>Reset pw</button>
                    <button onClick={() => remove(u)} className="text-[#A1A19D] hover:text-[#e07a7a]"><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title="Add user" testid="user-create-modal">
        <Field label="Email"><input className={inputCls} data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Name"><input className={inputCls} data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Write role"><select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{ROLE_OPTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
        <Field label="Audience scopes">
          <div className="flex gap-2">
            {AUDIENCE_OPTS.map((a) => (
              <button key={a} type="button" onClick={() => toggleFormAudience(a)}
                className={`rounded-sm border px-3 py-1 text-xs ${form.audiences.includes(a) ? "border-[#F59E0B] bg-[rgba(245,158,11,0.12)] text-[#F59E0B]" : "border-[#2C2C2A] text-[#A1A19D]"}`}>{a}</button>
            ))}
          </div>
        </Field>
        <Field label="Temporary password (min 10 chars)"><input type="password" className={inputCls} data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
        <p className="mb-4 text-xs text-[#A1A19D]">No email is sent. Share the temporary password securely; the user must change it after first sign-in.</p>
        <div className="flex justify-end gap-2"><GhostButton onClick={() => setCreating(false)}>Cancel</GhostButton><PrimaryButton data-testid="user-create-save" onClick={create}>Create</PrimaryButton></div>
      </Modal>

      <Modal open={!!resetFor} onClose={() => setResetFor(null)} title={`Reset password · ${resetFor?.email}`} testid="user-reset-modal">
        <Field label="New temporary password"><input type="password" className={inputCls} data-testid="reset-pw" value={resetPw} onChange={(e) => setResetPw(e.target.value)} /></Field>
        <div className="flex justify-end gap-2"><GhostButton onClick={() => setResetFor(null)}>Cancel</GhostButton><PrimaryButton data-testid="reset-save" onClick={doReset}>Reset</PrimaryButton></div>
      </Modal>
    </Section>
  );
}

function Invitations() {
  const [invites, setInvites] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", role: "viewer", audiences: ["internal"], note: "" });
  const load = useCallback(async () => { const { data } = await api.get("/api/invitations"); setInvites(data); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await api.post("/api/invitations", form); toast.success("Invitation recorded (no email sent)"); setCreating(false); setForm({ email: "", role: "viewer", audiences: ["internal"], note: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const remove = async (i) => { try { await api.delete(`/api/invitations/${i.id}`); load(); } catch { toast.error("Failed"); } };
  return (
    <Section title="Pending invitations (no emails sent)" action={<PrimaryButton data-testid="invite-add-btn" onClick={() => setCreating(true)}><span className="inline-flex items-center gap-1"><Plus size={14} /> Record invitation</span></PrimaryButton>}>
      <p className="mb-4 text-sm text-[#A1A19D]">Invitations are recorded here for the admin's reference only. No email is dispatched; access is granted exclusively by the admin creating an account.</p>
      {invites.length === 0 ? <EmptyState title="No pending invitations" testid="invites-empty" /> : (
        <div className="overflow-x-auto border border-[#2C2C2A]">
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr className="border-b border-[#2C2C2A] text-left"><th className="cm-label p-3">Email</th><th className="cm-label p-3">Role</th><th className="cm-label p-3">Audiences</th><th className="cm-label p-3">Email sent</th><th className="cm-label p-3 text-right">Actions</th></tr></thead>
            <tbody>
              {invites.map((i) => (
                <tr key={i.id} className="border-b border-[#2C2C2A] last:border-0" data-testid={`invite-row-${i.email}`}>
                  <td className="p-3 text-[#F9F9F8]">{i.email}</td>
                  <td className="p-3 text-[#A1A19D]">{i.role}</td>
                  <td className="p-3 text-[#A1A19D]">{i.audiences.join(", ")}</td>
                  <td className="p-3"><StatusBadge status={i.email_sent ? "Sent" : "No"} /></td>
                  <td className="p-3 text-right"><button onClick={() => remove(i)} className="text-[#A1A19D] hover:text-[#e07a7a]"><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={creating} onClose={() => setCreating(false)} title="Record invitation" testid="invite-create-modal">
        <Field label="Email"><input className={inputCls} data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Role"><select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{ROLE_OPTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
        <Field label="Note"><input className={inputCls} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
        <div className="flex justify-end gap-2"><GhostButton onClick={() => setCreating(false)}>Cancel</GhostButton><PrimaryButton data-testid="invite-create-save" onClick={create}>Record</PrimaryButton></div>
      </Modal>
    </Section>
  );
}

function AuditLog() {
  const [log, setLog] = useState([]);
  useEffect(() => { api.get("/api/audit", { params: { limit: 100 } }).then((r) => setLog(r.data)).catch(() => {}); }, []);
  return (
    <Section title="Audit log">
      {log.length === 0 ? <EmptyState title="No activity yet" /> : (
        <div className="max-h-96 overflow-y-auto border border-[#2C2C2A]">
          {log.map((e) => (
            <div key={e.id} className="flex items-center justify-between border-b border-[#2C2C2A] px-4 py-2.5 text-sm last:border-0" data-testid="audit-entry">
              <div><span className="text-[#F9F9F8]">{e.actor_email}</span> <span className="text-[#A1A19D]">{e.action} {e.entity}</span> {e.detail && <span className="text-[#6b6b66]">· {e.detail}</span>}</div>
              <div className="cm-label whitespace-nowrap">{new Date(e.at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { currentId } = useProjects();
  const admin = isAdmin(user);

  const exportSummary = async () => {
    try {
      const { data } = await api.get(`/api/projects/${currentId}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${currentId}-summary.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Nonsensitive summary exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Settings" title="Settings" description="Account security, role & audience model, pending invitations, and the audit log." />

      <Section title="Account security"><ChangePassword /></Section>

      <Section title="Access model">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="border border-[#2C2C2A] bg-[#1A1A18] p-6">
            <div className="cm-label mb-3">Write roles</div>
            <ul className="space-y-2 text-sm text-[#A1A19D]">
              <li><span className="text-[#F9F9F8]">Admin</span> — manages users, invitations, deletes records, full write.</li>
              <li><span className="text-[#F9F9F8]">Editor</span> — edits project work (finance, closeout, production, documents, credits).</li>
              <li><span className="text-[#F9F9F8]">Viewer</span> — read-only.</li>
            </ul>
          </div>
          <div className="border border-[#2C2C2A] bg-[#1A1A18] p-6">
            <div className="cm-label mb-3">Audience scopes (independent of write role)</div>
            <ul className="space-y-2 text-sm text-[#A1A19D]">
              <li><span className="text-[#F9F9F8]">Internal</span> — default. Sees all records including financial/personnel/source material.</li>
              <li><span className="text-[#F9F9F8]">Investor / Creator</span> — sees only material explicitly approved for that audience. No bank, payroll, tax, card or ACH material is ever exposed by a role change.</li>
            </ul>
            <p className="mt-3 text-xs text-[#6b6b66]">All private content currently defaults to Internal. No new audience has been granted access yet.</p>
          </div>
        </div>
      </Section>

      <Section title="Export">
        <div className="border border-[#2C2C2A] bg-[#1A1A18] p-6 flex items-center justify-between">
          <p className="text-sm text-[#A1A19D] max-w-xl">Export a nonsensitive project summary (journey, closeout status, production status, nonrestricted document index). Restricted accounting/payroll/PII material and unverified financial figures are excluded.</p>
          <PrimaryButton data-testid="export-btn" onClick={exportSummary}><span className="inline-flex items-center gap-1"><DownloadSimple size={14} /> Export JSON</span></PrimaryButton>
        </div>
      </Section>

      {admin ? (
        <>
          <Users />
          <Invitations />
          <AuditLog />
        </>
      ) : (
        <Section title="Administration">
          <EmptyState title="Admin only" description="User management, invitations and the audit log are restricted to admins." />
        </Section>
      )}
    </div>
  );
}
